import React, {useState, useEffect} from 'react';
import PropTypes from 'prop-types';
//MUI
import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';
import InputAdornment from '@material-ui/core/InputAdornment';
//ICONS
import SearchIcon from '@material-ui/icons/Search';

/**
 * Realiza una busqueda en un arreglo de arboles con nodos anidados uno dentro de otro de acuerdo a las propiedades proporcionadas.
 *
 * Tambien soporta las siguientes propiedades de [TextField](https://material-ui.com/api/text-field/):
 *
 * - variant
 * - label
 * - error
 * - helperText
 * - InputProps(menos la propiedad interna ednAdornment)
 *
 * Cualquier otra propiedad no especificada en la documentacion sera reenviada al componente [Autocomplete](https://material-ui.com/components/autocomplete/).
 */
function BuscadorArboles({
   arboles,
   mapaArbol,
   valorIgnorar,
   soloHojas,
   itemComponent,
   variant,
   label,
   error,
   helperText,
   InputProps,
   ...rest
}) {
   //Controlar la lista de arboles a buscar en base a una lista de arboles.
   const [lista, setLista] = useState([]);
   /**
    * Agrega la información de `nodo` a `arreglo`
    */
   function agregarNodoToArreglo(nodo, arreglo) {
      arreglo.push({
         [mapaArbol.titulo]: nodo[mapaArbol.titulo],
         [mapaArbol.valorIgnorar]: nodo[mapaArbol.valorIgnorar]
      });
   }
   /**
    * Genera una lista en formato usable por el componenete para realizar busquedas en base a un arbol
    */
   function arregloArbol(arbol) {
      let arregloFinal = [];
      let arregloParcial = [];
      const esHoja = !(arbol[mapaArbol.nodos] && arbol[mapaArbol.nodos].length > 0);
      if (valorIgnorar) {
         if (arbol[mapaArbol.valorIgnorar] && arbol[mapaArbol.valorIgnorar] !== valorIgnorar) {
            if (soloHojas) {
               if (esHoja) {
                  agregarNodoToArreglo(arbol, arregloFinal);
               }
            } else {
               agregarNodoToArreglo(arbol, arregloFinal);
            }
         }
      } else {
         agregarNodoToArreglo(arbol, arregloFinal);
      }
      if (!esHoja) {
         for (let inxPages = 0; inxPages < arbol[mapaArbol.nodos].length; inxPages++) {
            const nodo = arbol[mapaArbol.nodos][inxPages];
            const arregloNodo = arregloArbol(nodo);
            arregloParcial = [...arregloParcial, ...arregloNodo];
         }
      }
      return [...arregloFinal, ...arregloParcial];
   }
   useEffect(() => {
      if (arboles.length > 0) {
         let nuevaLista = [];
         for (let inxModulos = 0; inxModulos < arboles.length; inxModulos++) {
            const modulo = arboles[inxModulos];
            const listaModulo = arregloArbol(modulo);
            nuevaLista = [...nuevaLista, ...listaModulo];
         }
         setLista(nuevaLista);
      }
   }, [arboles, mapaArbol, valorIgnorar, soloHojas]);
   return (
      <Autocomplete
         {...rest}
         options={lista}
         getOptionLabel={(option) => option[mapaArbol.titulo]}
         renderInput={(params) => {
            let {InputProps: ImputPropsCustom, ...otherParams} = params;
            ImputPropsCustom = {
               ...ImputPropsCustom,
               startAdornment: (
                  <InputAdornment position="start">
                     <SearchIcon />
                  </InputAdornment>
               ),
               ...InputProps
            };
            return (
               <TextField
                  variant={variant}
                  label={label}
                  error={error}
                  helperText={helperText}
                  InputProps={ImputPropsCustom}
                  {...otherParams}
               />
            );
         }}
      />
   );
}

BuscadorArboles.propTypes = {
   /**
    * Arreglo de arboles con nodos anidados uno dentro de otro.
    */
   arboles: PropTypes.arrayOf(PropTypes.object).isRequired,
   /**
    * Equivalencia de las propiedades usadas por el componente y las proporcionados por los nodos de los `arboles`, donde:
    *
    * - `titulo`: Propiedad de los nodos de los `arboles` que contine el texto sobre el cual se realizara la busqueda.
    * - `valorIgnorar`: Propiedad de los nodos de los `arboles` que contine un valor de verificación para incluir el nodo en la busqueda.
    * - `nodos`: Propiedad de los nodos de los `arboles` que contine los nodos o hijos del nodo.
    *
    */
   mapaArbol: PropTypes.shape({
      titulo: PropTypes.string.isRequired,
      valorIgnorar: PropTypes.string.isRequired,
      nodos: PropTypes.string.isRequired
   }),
   /**
    * Valor cuya propiedad representada por `valorIgnorar` del nodo debe ser cumplida para ignorar el nodo en la busqueda, si no es proporcionada todos los nodos seran tomados en cuenta en la busqueda.
    */
   valorIgnorar: PropTypes.string,
   /**
    * Si es __true__ solo se realizara la busqueda en los nodos hojas de los arboles.
    */
   soloHojas: PropTypes.bool,
   /**
    * Elemento que renderizara los item hojas de la lista, este componente recibira las propiedades adicionales nod definidad en `mapaArbol`.
    */
   itemComponent: PropTypes.elementType
};
BuscadorArboles.defaultProps = {
   mapaArbol: {
      titulo: 'titulo',
      valorIgnorar: 'valorIgnorar',
      nodos: 'nodos'
   },
   soloHojas: false
};
export default BuscadorArboles;
